import { useEffect, useRef, useState } from 'react';
import './MyList.css';

// ============================================================
// MARC'S LIST — the owner's short task list, pinned above the board.
// A light panel (same surface family as the NEW rail). One row per open
// task: checkbox · project color bar · task · card number. Tap the row to
// open its note. Checked rows stay, crossed out, for a day, then drop off.
//
// Everything here is display + intent. The parent (Watch.js) owns the
// writes: check/uncheck ride the 5 s undo toast; add and note go straight
// through the relay. `onAdd(payload)` returns the server's answer, which
// this panel reads back: needs:'link' → show candidate cards, needs:'project'
// → show the project picker, otherwise the row appears.
// ============================================================

const CLOSE_HINT = 'write "close card" to close the card too';

function CardRef({ task, byWatchId, onJump }) {
  if (!task.watchId) return <span className="MyList-card MyList-card--none">no card</span>;
  const item = byWatchId.get(task.watchId);
  const dead = item && item.status !== 'Open';
  return (
    <button type="button" className={'MyList-card' + (dead ? ' MyList-card--closed' : '')} title={item ? item.title : `card #${task.watchId}`}
      onClick={(e) => { e.stopPropagation(); onJump?.(task.watchId); }}>
      #{task.watchId}{dead ? ' · closed' : ''}
    </button>
  );
}

function Row({ task, byWatchId, colorOf, open, onToggle, onCheck, onUncheck, onNote, onDrop, onJump }) {
  const done = task.status === 'Done';
  const item = task.watchId ? byWatchId.get(task.watchId) : null;
  const color = colorOf(item || { projectId: task.projectId });
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const stale = item && item.status !== 'Open' && !done;
  const standing = item?.synopsis?.standing || '';

  const submitNote = async () => {
    const n = draft.trim();
    if (!n || busy) return;
    setBusy(true);
    try { await onNote(task, n); setDraft(''); } finally { setBusy(false); }
  };

  return (
    <div className={'MyList-row' + (done ? ' MyList-row--done' : '') + (open ? ' MyList-row--open' : '') + (stale ? ' MyList-row--stale' : '')} data-testid={`mylist-row-${task.taskId}`}>
      <div className="MyList-line" role="button" tabIndex={0} onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}>
        <input type="checkbox" className="MyList-box" checked={done} aria-label={done ? `reopen ${task.task}` : `done: ${task.task}`}
          onClick={(e) => e.stopPropagation()} onChange={() => (done ? onUncheck(task) : onCheck(task))} />
        <span className="MyList-bar" style={{ background: color }} aria-hidden="true" />
        <span className="MyList-task">
          <span className="MyList-task-text">{task.task}</span>
          {stale && <span className="MyList-stale-chip">Watch closed this card</span>}
          {!open && task.note && <span className="MyList-note-peek">{lastNoteLine(task.note)}</span>}
        </span>
        <CardRef task={task} byWatchId={byWatchId} onJump={onJump} />
      </div>
      {open && (
        <div className="MyList-detail">
          {standing && <div className="MyList-standing"><span className="MyList-k">card</span>{standing}</div>}
          {task.note ? <pre className="MyList-note">{task.note}</pre> : <div className="MyList-note MyList-note--empty">no note yet</div>}
          <div className="MyList-note-add">
            <input type="text" className="MyList-input" id={`mylist-note-${task.taskId}`} placeholder={done ? 'add a note' : `add a note · ${CLOSE_HINT}`} value={draft}
              onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submitNote(); }} disabled={busy} />
            <button type="button" className="MyList-btn" onClick={submitNote} disabled={busy || !draft.trim()}>{busy ? '…' : 'Add note'}</button>
            {!done && <button type="button" className="MyList-btn MyList-btn--quiet" onClick={() => onDrop(task)}>Drop</button>}
          </div>
        </div>
      )}
    </div>
  );
}

export function lastNoteLine(note) {
  const lines = String(note || '').trim().split('\n').filter(Boolean);
  return (lines[lines.length - 1] || '').replace(/^\[[^\]]+\]\s*/, '');
}

function AddForm({ projects, colorOfProject, onAdd, onClose }) {
  const [task, setTask] = useState('');
  const [note, setNote] = useState('');
  const [projectId, setProjectId] = useState('');
  const [answer, setAnswer] = useState(null);     // the server's reply when it needs something
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);

  const send = async (extra = {}) => {
    const t = task.trim();
    if (!t || busy) return;
    setBusy(true); setError(null);
    try {
      const payload = { action: 'add', task: t, ...(note.trim() ? { note: note.trim() } : {}), ...(projectId ? { projectId } : {}), ...extra };
      const r = await onAdd(payload);
      if (r?.needs === 'link' || r?.needs === 'project') { setAnswer(r); return; }
      onClose();
    } catch (e) { setError(e?.message || String(e)); } finally { setBusy(false); }
  };

  return (
    <div className="MyList-add" data-testid="mylist-add">
      <div className="MyList-add-line">
        <input ref={ref} id="mylist-add-task" type="text" className="MyList-input MyList-input--task" placeholder="What needs doing? Short." value={task}
          onChange={(e) => { setTask(e.target.value); setAnswer(null); }} onKeyDown={(e) => { if (e.key === 'Enter') send(); if (e.key === 'Escape') onClose(); }} disabled={busy} />
        <select id="mylist-add-project" className="MyList-select" value={projectId} onChange={(e) => { setProjectId(e.target.value); setAnswer(null); }} aria-label="project">
          <option value="">project: let Watch guess</option>
          {projects.map((p) => <option key={p.projectId} value={p.projectId}>{p.label}</option>)}
        </select>
        <button type="button" className="MyList-btn MyList-btn--go" onClick={() => send()} disabled={busy || !task.trim()}>{busy ? '…' : 'Add'}</button>
        <button type="button" className="MyList-btn MyList-btn--quiet" onClick={onClose}>Cancel</button>
      </div>
      <input id="mylist-add-note" type="text" className="MyList-input" placeholder="note (optional)" value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} />
      {error && <div className="MyList-error" role="alert">{error}</div>}
      {answer?.needs === 'link' && (
        <div className="MyList-ask">
          <div className="MyList-ask-title">{answer.candidates?.length ? 'Is this one of these cards?' : 'No card looks like this.'}</div>
          {(answer.candidates || []).map((c) => (
            <button key={c.watchId} type="button" className="MyList-cand" onClick={() => send({ watchId: c.watchId })} disabled={busy}>
              <span className="MyList-bar" style={{ background: colorOfProject(c.projectId) }} aria-hidden="true" />
              <span className="MyList-cand-title">{c.title}</span>
              <span className="MyList-card">#{c.watchId}{c.court ? ` · ${c.court}` : ''}{c.triaged ? '' : ' · NEW'}</span>
            </button>
          ))}
          <button type="button" className="MyList-btn" onClick={() => send({ newCard: true })} disabled={busy}>Make a new card</button>
        </div>
      )}
      {answer?.needs === 'project' && (
        <div className="MyList-ask">
          <div className="MyList-ask-title">Which project does the new card belong to?</div>
          <div className="MyList-proj-grid">
            {(answer.projects || []).map((p) => (
              <button key={p.projectId} type="button" className="MyList-cand" onClick={() => { setProjectId(p.projectId); send({ newCard: true, projectId: p.projectId }); }} disabled={busy}>
                <span className="MyList-bar" style={{ background: colorOfProject(p.projectId) }} aria-hidden="true" />
                <span className="MyList-cand-title">{p.parentId ? '· ' : ''}{p.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MyList({ owner = 'Marc', tasks, byWatchId, colorOf, colorOfProject, projects, error, onCheck, onUncheck, onNote, onDrop, onAdd, onJump }) {
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const openCount = tasks.filter((t) => t.status === 'Open').length;
  const doneCount = tasks.length - openCount;

  return (
    <section className="MyList" aria-label={`${owner}'s list`} data-testid="mylist">
      <div className="MyList-head">
        <span className="MyList-title">{owner.toUpperCase()}'S LIST</span>
        <span className="MyList-counts">{openCount} open{doneCount ? ` · ${doneCount} done` : ''}</span>
        <span className="MyList-spacer" />
        {!adding && <button type="button" className="MyList-btn MyList-btn--add" onClick={() => setAdding(true)}>+ add</button>}
      </div>
      {error && <div className="MyList-error" role="alert">List: {error}</div>}
      {adding && <AddForm projects={projects} colorOfProject={colorOfProject} onAdd={onAdd} onClose={() => setAdding(false)} />}
      {tasks.length === 0 && !adding && !error && (
        <div className="MyList-empty">Nothing on your list. Add one here, or tell chat "add to my list: …".</div>
      )}
      {tasks.map((t) => (
        <Row key={t.taskId || t.id} task={t} byWatchId={byWatchId} colorOf={colorOf} open={openId === t.taskId}
          onToggle={() => setOpenId((v) => (v === t.taskId ? null : t.taskId))}
          onCheck={onCheck} onUncheck={onUncheck} onNote={onNote} onDrop={onDrop} onJump={onJump} />
      ))}
    </section>
  );
}
